import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageContext } from '@/contexts/page-context';
import { useCurrentUser } from '@/hooks/use-current-user';
import { accountSettingsService } from '@/services/account-settings-service';
import {
  Header,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Spinner,
  Empty,
  EmptyTitle,
  EmptyDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Field,
  FieldLabel,
  Input,
  toast,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'prosci-ui';
import {
  Lightning,
  Plus,
  ArrowSquareOut,
  MoreHorizontal,
  Trash2,
  Copy,
} from '@/lib/icons';
import { apiClient } from '@/utils/api-client';
import type { ApiKeyResource } from '@/types/external-api-keys';
import { KaiyaMcpSettingsTab } from '@/features/kaiya-mcp/kaiya-mcp-settings-tab';
import { ProximaPortfolioApiKeysTab } from '@/features/proxima-api/proxima-portfolio-api-keys-tab';
import { ProximaProjectApiKeysTab } from '@/features/proxima-api/proxima-project-api-keys-tab';
import { ProximaProjectSettingsTab } from '@/features/proxima-api/proxima-project-settings-tab';
import { ProximaUsageHistory } from '@/features/proxima-api/proxima-usage-history';
import { CodeIcon } from '@phosphor-icons/react';

const API_DOCS_URL = import.meta.env.VITE_KAIYA_API_DOC_URL || '';
const PROXIMA_API_DOCS_URL = import.meta.env.VITE_PROXIMA_API_DOC_URL;

interface UsageStatsResponse {
  data: {
    type: string;
    attributes: {
      current_period: {
        month: string;
        endpoints: {
          chat: {
            used: number;
            limit: number;
            percentage: number;
          };
          documents: {
            used: number;
            limit: number;
            percentage: number;
          };
        };
      };
      daily_usage: {
        dates: string[];
        endpoints: {
          chat: number[];
          documents: number[];
        };
      };
      limits: {
        chat: {
          per_minute: number;
          per_month: number;
        };
        documents: {
          per_minute: number;
          per_month: number;
        };
      };
    };
  };
}

export function IntegrationsPage() {
  const { setShowPageHeader } = usePageContext();
  const { data: currentAdmin } = useCurrentUser();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [activeProximaTab, setActiveProximaTab] = useState('portfolio');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [keyToDelete, setKeyToDelete] = useState<ApiKeyResource | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);

  const organizationId = currentAdmin?.organization_id;

  const { data: accountSettings, isLoading: settingsLoading, isError: settingsError } = useQuery({
    queryKey: ['accountSettings', organizationId],
    queryFn: () => accountSettingsService.getSettings(organizationId!),
    enabled: !!organizationId,
  });

  const hasKaiyaApi = accountSettings?.enabled_features?.some((f) => f.code === 'kaiya_api') ?? false;
  // Kaiya MCP is a paid add-on; its tab should stay reachable whenever the account is
  // entitled, including when the admin has set the mode to "disabled". In that state the
  // assignment is enabled: false so it drops out of enabled_features, but kaiya_mcp_mode
  // is only ever 'disabled' when an assignment exists (see resolve_feature_mode), so it's
  // a reliable "entitled but turned off" signal that keeps the admin from locking itself out.
  const hasKaiyaMcp =
    (accountSettings?.enabled_features?.some((f) => f.code === 'kaiya_mcp') ?? false) ||
    accountSettings?.kaiya_mcp_mode === 'disabled';
  const hasProximaApi = accountSettings?.enabled_features?.some((f) => f.code === 'proxima_api') ?? false;
  const proximaApiMode = accountSettings?.proxima_api_mode;
  const isProximaProjectEnabled = hasProximaApi && proximaApiMode !== 'disabled';

  useEffect(() => {
    if (!settingsLoading && activeTab === null) {
      if (!settingsError && hasKaiyaApi) {
        setActiveTab('kaiya');
      } else if (!settingsError && hasKaiyaMcp) {
        setActiveTab('kaiya-mcp');
      } else {
        setActiveTab('proxima');
      }
    }
  }, [settingsLoading, settingsError, hasKaiyaApi, hasKaiyaMcp, activeTab]);

  useEffect(() => {
    if (!isProximaProjectEnabled && activeProximaTab === 'project') {
      setActiveProximaTab('portfolio');
    }
  }, [isProximaProjectEnabled, activeProximaTab]);

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Fetch API keys
  const { data: apiKeysData, isLoading: keysLoading } = useQuery({
    queryKey: ['api-keys', organizationId],
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ data: ApiKeyResource[] }>(
          '/api/external_api/kaiya_keys',
          { organization_id: organizationId }
        );
        return response.data || [];
      } catch (error: unknown) {
        // Return empty array for 400/404 errors (no data yet)
        const status = (error as { status?: number })?.status;
        if (status === 400 || status === 404) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!organizationId,
    staleTime: 30000,
  });

  // Fetch usage data (includes current period, daily usage, and limits)
  const { data: usageStats, isLoading: usageLoading } = useQuery({
    queryKey: ['api-keys-usage', organizationId],
    queryFn: async () => {
      try {
        const response = await apiClient.get<UsageStatsResponse>(
          '/api/external_api/kaiya_usage',
          { organization_id: organizationId }
        );
        return response.data?.attributes;
      } catch (error: unknown) {
        // Return undefined for 400/404 errors (no data yet or no API keys)
        const status = (error as { status?: number })?.status;
        if (status === 400 || status === 404) {
          return undefined;
        }
        throw error;
      }
    },
    enabled: !!organizationId,
    staleTime: 60000,
  });

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await apiClient.post<{ data: ApiKeyResource }>(
        '/api/external_api/kaiya_keys',
        { organization_id: organizationId, name }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', organizationId] });
      setNewlyCreatedKey(data.attributes.key);
      setNewKeyName('');
      toast.success('API key created successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create API key');
    },
  });

  // Delete API key mutation
  const deleteKeyMutation = useMutation({
    mutationFn: async (keyId: string) => {
      await apiClient.delete(`/api/external_api/api_keys/${keyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', organizationId] });
      setShowDeleteDialog(false);
      setKeyToDelete(null);
      toast.success('API key deleted successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete API key');
    },
  });

  const MAX_API_KEYS = 5;
  const hasReachedMaxKeys = (apiKeysData?.length || 0) >= MAX_API_KEYS;

  const handleCreateKey = () => {
    if (hasReachedMaxKeys) {
      toast.error(`You can only create a maximum of ${MAX_API_KEYS} API keys`);
      return;
    }
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    createKeyMutation.mutate(newKeyName.trim());
  };

  const handleDeleteKey = () => {
    if (keyToDelete) {
      deleteKeyMutation.mutate(keyToDelete.id);
    }
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success('API key copied to clipboard');
    } catch {
      toast.error('Failed to copy API key');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatMonth = (monthStr: string) => {
    // monthStr is like "2026-01"
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = new Date(parseInt(year), parseInt(month), 0);
    return `${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  };

  if (settingsLoading || activeTab === null) {
    return (
      <>
        <Header title="Integrations" size="small" />
        <div className="flex justify-center py-16">
          <Spinner size="large" />
        </div>
      </>
    );
  }

  if (settingsError) {
    return (
      <>
        <Header title="Integrations" size="small" />
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <p className="text-destructive font-medium">Failed to load integration settings</p>
          <p className="text-sm text-muted-foreground">Please refresh the page to try again.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Integrations" size="small" />

      <div className="bg-white border-b border-accent-sand">
        <div className="max-w-[1160px] px-6 mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="kaiya" disabled={!hasKaiyaApi}>Kaiya API</TabsTrigger>
              <TabsTrigger value="kaiya-mcp" disabled={!hasKaiyaMcp}>Kaiya MCP</TabsTrigger>
              <TabsTrigger value="proxima">Proxima API</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <ContentContainer>
        {activeTab === 'kaiya-mcp' && <KaiyaMcpSettingsTab />}

        {activeTab === 'proxima' && (
          <div className="space-y-6">
            <div className="bg-[#E8F4F4] border border-[#87ADB2] rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#87ADB2] flex items-center justify-center">
                  <CodeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold">Developer Docs</p>
                  <p className="text-sm">
                    Looking to develop an integration with Proxima? Our API documentation will help you get started.
                  </p>
                </div>
              </div>
              <Button
                asChild
                variant="default"
                size="sm"
              >
                <a href={PROXIMA_API_DOCS_URL} target="_blank" rel="noopener noreferrer">
                  <ArrowSquareOut className="h-4 w-4" />
                  API Documentation
                </a>
              </Button>
            </div>

            <Card>
              <CardContent className="pt-0">
                <Tabs value={activeProximaTab} onValueChange={setActiveProximaTab}>
                  <div className="border-b">
                    <TabsList className="border-0">
                      <TabsTrigger value="portfolio">Portfolio API Keys</TabsTrigger>
                      <TabsTrigger value="project" disabled={!isProximaProjectEnabled}>
                        Project API Keys
                      </TabsTrigger>
                    </TabsList>
                  </div>
                  <TabsContent value="portfolio" className="mt-6">
                    <ProximaPortfolioApiKeysTab />
                  </TabsContent>
                  <TabsContent value="project" className="mt-6">
                    <ProximaProjectApiKeysTab />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            <ProximaProjectSettingsTab />
            <ProximaUsageHistory />

          </div>
        )}

        {activeTab === 'kaiya' && (
          <div className="space-y-6">
            {/* Beta Feature Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Lightning className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">Beta Feature</p>
                  <p className="text-sm text-amber-700">
                    Kaiya API is currently in beta. Rate limits and features may change.
                  </p>
                </div>
              </div>
              <Button
                asChild
                variant="default"
                size="sm"
              >
                <a href={API_DOCS_URL} target="_blank" rel="noopener noreferrer">
                  <ArrowSquareOut className="h-4 w-4" />
                  API Documentation
                </a>
              </Button>
            </div>

            {/* API Keys Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>API Keys</CardTitle>
                    <CardDescription>
                      Manage your organization's API keys for Kaiya integration ({apiKeysData?.length || 0}/{MAX_API_KEYS})
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (hasReachedMaxKeys) {
                        toast.error(`Only ${MAX_API_KEYS} keys maximum are allowed`);
                        return;
                      }
                      setShowCreateDialog(true);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Create New Key
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {keysLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="small" />
                  </div>
                ) : apiKeysData && apiKeysData.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>NAME</TableHead>
                          <TableHead>KEY</TableHead>
                          <TableHead>CREATED</TableHead>
                          <TableHead>LAST USED</TableHead>
                          <TableHead>STATUS</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {apiKeysData.map((apiKey) => (
                          <TableRow key={apiKey.id}>
                            <TableCell className="font-medium">
                              {apiKey.attributes.name || 'Unnamed Key'}
                            </TableCell>
                            <TableCell>
                              <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                                {apiKey.attributes.masked_key}
                              </code>
                            </TableCell>
                            <TableCell>{formatDate(apiKey.attributes.created_at)}</TableCell>
                            <TableCell>
                              {apiKey.attributes.last_used_at
                                ? formatDate(apiKey.attributes.last_used_at)
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="success" tint>
                                Active
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onClick={() => {
                                      setKeyToDelete(apiKey);
                                      setShowDeleteDialog(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete Key
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2">
                      <span className="text-amber-500">💡</span>
                      API keys provide full access to your organization's Kaiya instance. Keep them secure and never share them publicly.
                    </p>
                  </>
                ) : (
                  <Empty>
                    <EmptyTitle>No API keys yet</EmptyTitle>
                    <EmptyDescription>
                      Create your first API key to start integrating with Kaiya.
                    </EmptyDescription>
                    <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                      <Plus className="h-4 w-4" />
                      Create New Key
                    </Button>
                  </Empty>
                )}
              </CardContent>
            </Card>

            {/* Current Period Usage */}
            <Card>
              <CardHeader>
                <CardTitle>Current Period</CardTitle>
                {usageStats?.current_period?.month && (
                  <CardDescription>
                    {formatMonth(usageStats.current_period.month)}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {usageLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="small" />
                  </div>
                ) : usageStats?.current_period ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Chat API Usage */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-primary"></div>
                          <span className="font-semibold">Chat API</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {usageStats.current_period.endpoints.chat.used.toLocaleString()} / {usageStats.current_period.endpoints.chat.limit.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(usageStats.current_period.endpoints.chat.percentage, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {usageStats.current_period.endpoints.chat.percentage.toFixed(1)}% of monthly limit
                      </p>
                    </div>

                    {/* Documents API Usage */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="font-semibold">Documents API</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {usageStats.current_period.endpoints.documents.used.toLocaleString()} / {usageStats.current_period.endpoints.documents.limit.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(usageStats.current_period.endpoints.documents.percentage, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {usageStats.current_period.endpoints.documents.percentage.toFixed(1)}% of monthly limit
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No usage data available.</p>
                )}
              </CardContent>
            </Card>

            {/* Usage History */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Usage History</CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <svg width="16" height="2" className="shrink-0">
                        <line x1="0" y1="1" x2="16" y2="1" stroke="#3b82f6" strokeWidth="2" />
                      </svg>
                      <span>Chat</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg width="16" height="2" className="shrink-0">
                        <line x1="0" y1="1" x2="16" y2="1" stroke="#22c55e" strokeWidth="2" strokeDasharray="4 2" />
                      </svg>
                      <span>Documents</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {usageStats?.daily_usage?.dates && usageStats.daily_usage.dates.length > 0 ? (
                  (() => {
                    const maxValue = Math.max(
                      ...usageStats.daily_usage.endpoints.chat,
                      ...usageStats.daily_usage.endpoints.documents,
                      1
                    );
                    const chartHeight = 200;
                    const padding = { top: 10, bottom: 30, left: 50, right: 20 };
                    const dataPoints = usageStats.daily_usage.dates.length;

                    // Generate points for SVG polyline
                    const generatePoints = (values: number[]) => {
                      return values.map((value, index) => {
                        const x = ((index / (dataPoints - 1 || 1)) * 100);
                        const y = 100 - ((value / maxValue) * 100);
                        return `${x},${y}`;
                      }).join(' ');
                    };

                    const chatPoints = generatePoints(usageStats.daily_usage.endpoints.chat);
                    const docsPoints = generatePoints(usageStats.daily_usage.endpoints.documents);

                    return (
                      <div className="relative" style={{ height: chartHeight + padding.top + padding.bottom }}>
                        {/* Y-axis labels */}
                        <div className="absolute left-0 top-0 w-12 flex flex-col justify-between text-xs text-muted-foreground" style={{ height: chartHeight, paddingTop: padding.top }}>
                          <span>{maxValue}</span>
                          <span>{Math.round(maxValue / 2)}</span>
                          <span>0</span>
                        </div>

                        {/* Chart area */}
                        <div className="absolute border-l border-b border-gray-200" style={{
                          left: padding.left,
                          right: padding.right,
                          top: padding.top,
                          height: chartHeight
                        }}>
                          <svg
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            className="w-full h-full"
                            style={{ overflow: 'visible' }}
                          >
                            {/* Grid lines */}
                            <line x1="0" y1="50" x2="100" y2="50" stroke="#e5e7eb" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />

                            {/* Documents line - dashed, rendered first (behind) */}
                            <polyline
                              points={docsPoints}
                              fill="none"
                              stroke="#22c55e"
                              strokeWidth="2"
                              strokeDasharray="4 2"
                              vectorEffect="non-scaling-stroke"
                              strokeLinejoin="round"
                              strokeLinecap="round"
                            />

                            {/* Chat line - solid, rendered on top */}
                            <polyline
                              points={chatPoints}
                              fill="none"
                              stroke="#3b82f6"
                              strokeWidth="2"
                              vectorEffect="non-scaling-stroke"
                              strokeLinejoin="round"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>

                        {/* X-axis labels */}
                        <div className="absolute flex justify-between text-xs text-muted-foreground" style={{
                          left: padding.left,
                          right: padding.right,
                          top: padding.top + chartHeight + 8
                        }}>
                          {usageStats.daily_usage.dates.map((date, index) => {
                            // Show first, last, and every 3rd label
                            if (index === 0 || index === usageStats.daily_usage.dates.length - 1 || index % 3 === 0) {
                              return (
                                <span
                                  key={index}
                                  className="whitespace-nowrap"
                                  style={{
                                    position: 'absolute',
                                    left: `${(index / (dataPoints - 1 || 1)) * 100}%`,
                                    transform: 'translateX(-50%)'
                                  }}
                                >
                                  {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No usage history available yet.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Rate Limits */}
            <Card>
              <CardHeader>
                <CardTitle>Rate Limits</CardTitle>
              </CardHeader>
              <CardContent>
                {usageStats?.limits ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ENDPOINT</TableHead>
                          <TableHead>PER MINUTE</TableHead>
                          <TableHead>PER MONTH</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-primary"></div>
                              <code className="font-mono">/v1/chat</code>
                            </div>
                          </TableCell>
                          <TableCell>{usageStats.limits.chat.per_minute} requests</TableCell>
                          <TableCell>{usageStats.limits.chat.per_month.toLocaleString()} requests</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <code className="font-mono">/v1/documents</code>
                            </div>
                          </TableCell>
                          <TableCell>{usageStats.limits.documents.per_minute} requests</TableCell>
                          <TableCell>{usageStats.limits.documents.per_month.toLocaleString()} requests</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <p className="text-muted-foreground">Rate limit information not available.</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create API Key Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) {
            setNewKeyName('');
            setNewlyCreatedKey(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {newlyCreatedKey ? 'API Key Created' : 'Create New API Key'}
              </DialogTitle>
              <DialogDescription>
                {newlyCreatedKey
                  ? 'Make sure to copy your API key now. You won\'t be able to see it again!'
                  : 'Enter a name for your new API key to help identify it later.'}
              </DialogDescription>
            </DialogHeader>

            {newlyCreatedKey ? (
              <div className="space-y-4">
                <div className="bg-gray-100 p-4 rounded-lg">
                  <code className="text-sm break-all">{newlyCreatedKey}</code>
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleCopyKey(newlyCreatedKey)}
                >
                  <Copy className="h-4 w-4" />
                  Copy API Key
                </Button>
              </div>
            ) : (
              <>
                <Field>
                  <FieldLabel htmlFor="keyName">Key Name</FieldLabel>
                  <Input
                    id="keyName"
                    placeholder="e.g., Production Integration"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </Field>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateKey}
                    disabled={createKeyMutation.isPending}
                  >
                    {createKeyMutation.isPending ? 'Creating...' : 'Create Key'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete API Key Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setKeyToDelete(null);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete API Key</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the API key "{keyToDelete?.attributes.name || 'Unnamed Key'}"?
                This action cannot be undone and any integrations using this key will stop working.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteKey}
                disabled={deleteKeyMutation.isPending}
              >
                {deleteKeyMutation.isPending ? 'Deleting...' : 'Delete Key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ContentContainer>
    </>
  );
}
